import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { CheckCircle, XCircle, Search } from 'lucide-react';
import ConfirmModal from './ConfirmModal';
import Pagination from './Pagination';

const Payments = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [paginationData, setPaginationData] = useState(null);

  // Modal state
  const [modal, setModal] = useState({ isOpen: false, action: null, transaction: null });

  const fetchTransactions = async (currentPage = 1) => {
    try {
      setLoading(true);
      const res = await api.get(`/api/admin/transactions?page=${currentPage}&limit=10`);
      setTransactions(res.data.data);
      setPaginationData(res.data.pagination);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions(page);
  }, [page]);

  const openConfirm = (tx, action) => {
    setModal({ isOpen: true, action, transaction: tx });
  };

  const handleConfirm = async () => {
    const { action, transaction: tx } = modal;
    setModal({ isOpen: false, action: null, transaction: null });
    setActionLoading(true);

    try {
      await api.post(`/api/admin/transaction/${action}`, {
        transactionId: tx.id,
        userId: tx.ownerId,
        type: tx.type
      });
      await fetchTransactions(page);
    } catch (err) {
      console.error(`Error ${action}ing transaction:`, err);
      alert(`Failed to ${action} transaction: ` + (err.response?.data?.message || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = () => {
    setModal({ isOpen: false, action: null, transaction: null });
  };

  const filteredTxs = transactions.filter(t =>
    t.pharmacyName?.toLowerCase().includes(filter.toLowerCase()) ||
    t.paymentRef?.toLowerCase().includes(filter.toLowerCase()) ||
    String(t.id).includes(filter)
  );

  return (
    <div className="space-y-8 sm:space-y-10">
      {/* Custom confirmation modal */}
      <ConfirmModal
        isOpen={modal.isOpen}
        action={modal.action}
        transaction={modal.transaction}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      {/* Page header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Payment Management</h1>

        <div className="relative w-full md:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search transactions..."
            className="pl-10 pr-4 py-2.5 sm:py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 w-full transition-all"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      </div>

      {actionLoading && (
        <div className="flex items-center gap-2 text-sm text-primary-600 bg-primary-50 p-3 rounded-xl">
          <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
          Processing transaction...
        </div>
      )}

      {/* Table / Card View */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Desktop Table View */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-left min-w-[700px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Pharmacy</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Type</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Reference</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Date</th>
                <th className="px-4 sm:px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-10 text-center text-gray-500">Loading transactions...</td>
                </tr>
              ) : filteredTxs.map((tx) => (
                <tr key={tx.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 sm:px-6 py-4 font-medium text-gray-900 text-sm">#{tx.id}</td>
                  <td className="px-4 sm:px-6 py-4 text-gray-700 text-sm max-w-[140px] truncate">{tx.pharmacyName}</td>
                  <td className="px-4 sm:px-6 py-4 hidden sm:table-cell">
                    <span className="bg-gray-100 text-gray-600 text-[10px] font-bold px-2 py-1 rounded">
                      {tx.type}
                    </span>
                  </td>
                  <td className="px-4 sm:px-6 py-4 font-semibold text-gray-900 text-sm whitespace-nowrap">৳{tx.amountBdt}</td>
                  <td className="px-4 sm:px-6 py-4 text-sm font-mono text-gray-500 hidden lg:table-cell">{tx.paymentRef || '—'}</td>
                  <td className="px-4 sm:px-6 py-4">
                    <StatusBadge status={tx.status} />
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-sm text-gray-500 hidden md:table-cell">
                    {new Date(tx.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 sm:px-6 py-4 text-right">
                    {tx.status === 'PENDING' && (
                      <div className="flex justify-end gap-1 sm:gap-2">
                        <button
                          onClick={() => openConfirm(tx, 'approve')}
                          className="p-1.5 sm:p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Approve"
                        >
                          <CheckCircle size={18} />
                        </button>
                        <button
                          onClick={() => openConfirm(tx, 'reject')}
                          className="p-1.5 sm:p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Reject"
                        >
                          <XCircle size={18} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && filteredTxs.length === 0 && (
                <tr>
                  <td colSpan="8" className="px-6 py-10 text-center text-gray-500">
                    No transactions found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="sm:hidden divide-y divide-gray-100">
          {loading ? (
            <div className="p-6 text-center text-gray-500">Loading transactions...</div>
          ) : filteredTxs.map((tx) => (
            <div key={tx.id} className="p-4 space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-bold text-gray-900 truncate max-w-[200px]">{tx.pharmacyName}</h4>
                  <p className="text-xs text-gray-400 font-mono mt-0.5">#{tx.id} • {new Date(tx.createdAt).toLocaleDateString()}</p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <p className="font-bold text-gray-900">৳{tx.amountBdt}</p>
                  <StatusBadge status={tx.status} />
                </div>
              </div>

              <div className="flex items-center justify-between pt-1">
                <div className="flex flex-col gap-1">
                  <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Type / Reference</span>
                  <p className="text-xs text-gray-600">
                    <span className="bg-gray-100 px-1.5 py-0.5 rounded mr-2">{tx.type}</span>
                    <span className="font-mono">{tx.paymentRef || 'No Reference'}</span>
                  </p>
                </div>

                {tx.status === 'PENDING' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => openConfirm(tx, 'approve')}
                      className="p-2 bg-green-50 text-green-600 rounded-lg"
                    >
                      <CheckCircle size={20} />
                    </button>
                    <button
                      onClick={() => openConfirm(tx, 'reject')}
                      className="p-2 bg-red-50 text-red-500 rounded-lg"
                    >
                      <XCircle size={20} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
          {!loading && filteredTxs.length === 0 && (
            <div className="p-6 text-center text-gray-500">No transactions found.</div>
          )}
        </div>

        <Pagination pagination={paginationData} onPageChange={setPage} />
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const styles = {
    PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
    SUCCESS: 'bg-green-50 text-green-700 border-green-200',
    FAILED: 'bg-red-50 text-red-700 border-red-200',
    REFUNDED: 'bg-gray-50 text-gray-700 border-gray-200',
  };

  return (
    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold border tracking-wider uppercase whitespace-nowrap ${styles[status] || styles.PENDING}`}>
      {status}
    </span>
  );
};

export default Payments;
